import { useState } from 'react';
import { useStore } from '../lib/store.js';
import { Avatar } from './Avatar.jsx';

export function ForwardPopover({ ev, threadURN, channelID, chatTo, onClose }) {
  const [step, setStep] = useState('pick'); // 'pick' | 'note'
  const [recipient, setRecipient] = useState(null);
  const [note, setNote] = useState('');
  const agents = useStore(s => s.agents?.list || []);
  const inbox = useStore(s => s.chat?.inbox || {});
  const enqueueChatSend = useStore(s => s.enqueueChatSend);
  const forwardAdd = useStore(s => s.forwardAdd);

  const msgID = ev?.event_id || ev?.body?.client_msg_id || '';
  const body = ev?.body || ev?.payload || {};
  const quote = ((typeof body.content === 'string' && body.content)
    || (typeof body.text === 'string' && body.text) || '').slice(0, 280);

  function handlePick(agent) {
    setRecipient(agent);
    setStep('note');
  }

  function handleSend() {
    if (!recipient || typeof enqueueChatSend !== 'function') return;
    const agentID = recipient.id || recipient.slug || recipient.name || '';
    const templated = `Forwarded from @${chatTo} · "${quote}" · Note: ${note}`;
    const recipientChannelID = `dm:${agentID}`;
    enqueueChatSend({
      channelID: recipientChannelID,
      threadURN: null,
      agent: agentID,
      message: templated,
    });
    forwardAdd({ threadURN, msgID, recipientAgent: agentID, note, channelID });

    // JOBE-0142.3: best-effort server-side annotation. Swallow all errors
    // so a network hiccup never blocks the forward UX.
    if (threadURN) {
      const msgSeq = typeof ev?.sequence_number === 'number' ? ev.sequence_number : 0;
      fetch(`/api/threads/${encodeURIComponent(threadURN)}/events`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_type: 'forward.created',
          recipientAgent: agentID,
          msgSeq,
          note,
        }),
      }).catch(() => { /* best-effort; swallow */ });
    }

    onClose();
  }

  if (step === 'pick') {
    return (
      <div className="v2-forward-popover" data-testid="v2-forward-popover">
        <div className="v2-forward-title">Forward to…</div>
        {agents.filter(a => (a.id || a.slug) !== chatTo).map(agent => {
          const agentID = agent.id || agent.slug || agent.name || '';
          const isFirstContact = !inbox[`dm:${agentID}`];
          return (
            <div key={agentID} className="v2-forward-agent-row"
              data-testid="v2-forward-agent-row">
              <Avatar name={agent.displayName || agentID} size={24} />
              <button type="button" className="v2-forward-agent-btn"
                onClick={() => handlePick(agent)}>
                {agent.displayName || agentID}
              </button>
              {isFirstContact && (
                <span className="v2-first-contact-chip" title="First contact with this agent">first contact</span>
              )}
            </div>
          );
        })}
        <button type="button" onClick={onClose} data-testid="v2-forward-cancel">Cancel</button>
      </div>
    );
  }

  const agentID = recipient?.id || recipient?.slug || recipient?.name || '';
  return (
    <div className="v2-forward-popover" data-testid="v2-forward-note-step">
      <div className="v2-forward-title">Note (optional)</div>
      <textarea
        value={note}
        onChange={e => setNote(e.target.value)}
        placeholder="Add a note…"
        data-testid="v2-forward-note"
      />
      <div className="v2-forward-actions">
        <button type="button" onClick={handleSend} data-testid="v2-forward-send-btn">
          Forward to {recipient?.displayName || agentID}
        </button>
        <button type="button" onClick={() => setStep('pick')} data-testid="v2-forward-back">Back</button>
      </div>
    </div>
  );
}
